import autobind from 'autobind-decorator';
import { navigateTo } from '#imports';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import {
  getUnreadCount,
  setMessageRead,
  setMessageReadAll,
} from '@core/api/message';
import { RouterService } from '@core/services/router.service';
import { ZoneService } from '@core/services/zone.service';
import type {
  MessageJumpVo,
  UnreadCountVo,
  UserMessageVo,
} from '@core/types/auto/forum';
import { MessageType, isNewFormatMessage } from '@core/utils/message';
import { getZoneBase } from '@core/utils/runtime-config';

@Injectable()
export class MessageService {
  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => ZoneService))
  public zoneService!: ZoneService;

  // 未读计数：铃铛角标与消息中心分类气泡共用同一数据源
  public unreadCount: UnreadCountVo = {};

  public init() {
    this.refreshUnreadCount();
  }

  public refreshUnreadCount() {
    getUnreadCount()
      .then((res) => {
        this.unreadCount = res || {};
      })
      .catch(() => {});
  }

  // 铃铛点击新标签页打开消息中心页（单表面，无下拉）
  // open 分支下对象形式经 router.resolve().href 解析，href 已含 base（zone 前缀），不可再手动拼前缀
  public goToMessageCenter() {
    navigateTo({ name: 'oj_message' }, { open: { target: '_blank' } });
  }

  // 整卡点击：跳转（如有）并置读；无跳转目标的通知仅置读
  public handleClickMessage(item: UserMessageVo) {
    const jump = isNewFormatMessage(item)
      ? item.jump
      : this.resolveV1Jump(item);
    if (jump) {
      this.handleMessageJump(item, jump);
    }
    return this.markRead(item);
  }

  public markRead(item: UserMessageVo) {
    if (item.isRead || item.id == null) return Promise.resolve();
    return setMessageRead(item.id)
      .then(() => {
        item.isRead = true;
        this.decrementUnread(item.category);
      })
      .catch(() => {});
  }

  public markAllRead() {
    return setMessageReadAll()
      .then(() => {
        this.unreadCount = {
          total: 0,
          likeCount: 0,
          commentCount: 0,
          noticeCount: 0,
        };
      })
      .catch(() => {});
  }

  private decrementUnread(category?: number) {
    const count = this.unreadCount;
    const dec = (value?: number) => Math.max(0, (value ?? 0) - 1);
    if (category === 1) count.likeCount = dec(count.likeCount);
    else if (category === 2) count.commentCount = dec(count.commentCount);
    else if (category === 3) count.noticeCount = dec(count.noticeCount);
    count.total = dec(count.total);
  }

  // v1 格式跳转映射，已冻结，不再新增场景
  // messageType 说明：管理删除1 用户回复2 题解审核通过4 审核不通过5 精华题解6 通过并加精7
  //   开通发帖权限11 移除发帖权限12 评论审核不通过13 超时未审核14
  //   用户奖项认证中15 通过未领取16 驳回17（3/8不出现在消息列表）
  private resolveV1Jump(item: UserMessageVo): MessageJumpVo | null {
    if (
      (
        [
          MessageType.USER_REPLY,
          MessageType.SOLUTION_AUDIT_PASS,
          MessageType.SOLUTION_FEATURED,
          MessageType.SOLUTION_AUDIT_PASS_AND_FEATURED,
          MessageType.COMMENT_AUDIT_REJECT,
        ] as number[]
      ).includes(item.messageType as number)
    ) {
      return {
        kind: 'route',
        name: 'discuss_post_detail',
        query: {
          id: String(item.posterId),
          type: String(item.posterType),
        },
        target: '_blank',
      };
    }
    if (item.messageType === MessageType.SOLUTION_AUDIT_REJECT) {
      // 审核不通过跳转个人主页-我的题解（我发布的）
      // todo：后续讨论也增加审核不通过时需跳转到我的讨论页面
      return {
        kind: 'route',
        name: 'oj_home_solution_published',
        target: '_blank',
      };
    }
    return null;
  }

  public handleMessageJump(item: UserMessageVo, jump: MessageJumpVo) {
    const target = jump.target ?? '_blank';
    if (jump.kind === 'url') {
      if (!jump.url) return;
      // 仅允许 http/https 外链，防御 javascript: 等危险 scheme
      if (!/^https?:\/\//i.test(jump.url)) return;
      if (target === '_self') {
        location.href = jump.url;
      } else {
        window.open(jump.url, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    if (!jump.name || !this.routerService.router.hasRoute(jump.name)) return;
    // fullPath 不含 router base（当前 zone 前缀），避免与 getZoneBase(zone) 叠加
    const fullPath = this.routerService.router.resolve({
      name: jump.name,
      query: jump.query,
    }).fullPath;
    // 消息 zone 缺失（null）时回退到当前浏览 zone，保证跳转落在有效域内
    const zone = item.zone || this.zoneService.zone;
    navigateTo(`${getZoneBase(zone)}${fullPath}`, { open: { target } });
  }

  // 昵称热区点击：仅跳转，不置读
  public handleInlineClick(item: UserMessageVo, jump: MessageJumpVo) {
    this.handleMessageJump(item, jump);
  }

  @autobind
  public getBellTrackData() {
    return {
      unread_count: this.unreadCount.total ?? 0,
    };
  }
}
